import { useState, useCallback, useMemo, useEffect } from 'react';
import { showConfirm } from '@/utils/common/alert';
import { ICommunityResource, RNFilePart, ICommunityResourceFile } from '@/types/admin/resources';

export interface EditResourceFormErrors {
  name?: string;
  description?: string;
  content?: string;
  icon?: string;
  url?: string;
  files?: string;
}

export interface ResourceFile {
  id: string;
  name: string;
  uri: string;
  type: string;
  size?: number;
  file?: RNFilePart; // For FormData
}

export interface EditResourceFormData {
  name: string;
  description: string;
  content: string;
  icon: string;
  url: string;
  position: number;
  files: ResourceFile[];
}

export const useEditResourceForm = (resource: ICommunityResource) => {
  const [formData, setFormData] = useState<EditResourceFormData>({
    name: resource.name,
    description: resource.description,
    content: resource.content,
    icon: resource.icon,
    url: resource.url,
    position: resource.position,
    files: [],
  });

  const [errors, setErrors] = useState<EditResourceFormErrors>({});
  const [originalData, setOriginalData] = useState<EditResourceFormData>();

  // Store original data for comparison
  useEffect(() => {
    // Convert existing files to our format (if any)
    const existingFiles: ResourceFile[] = resource.files ? 
      (resource.files as ICommunityResourceFile[]).map((file, index: number) => ({
        id: `existing_${file.id ?? index}`,
        name: file.label || `File ${file.id ?? index + 1}`,
        uri: file.fileUrl,
        type: 'application/octet-stream',
        size: undefined,
      })) : [];

    const original = {
      name: resource.name,
      description: resource.description,
      content: resource.content,
      icon: resource.icon,
      url: resource.url,
      position: resource.position,
      files: existingFiles,
    };
    setOriginalData(original);
    setFormData(original);
  }, [resource]);

  // Handle field changes
  const handleFieldChange = useCallback((field: keyof EditResourceFormData, value: string | number | ResourceFile[]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field as keyof EditResourceFormErrors]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }
  }, [errors]);

  // Handle appending files (for adding new files to existing ones)
  const handleAppendFiles = useCallback((newFiles: ResourceFile[]) => {
    setFormData(prev => ({
      ...prev,
      files: [...prev.files, ...newFiles],
    }));

    // Clear files error if it exists
    if (errors.files) {
      setErrors(prev => ({
        ...prev,
        files: undefined,
      }));
    }
  }, [errors.files]);

  // Validate form
  const validateForm = useCallback((): boolean => {
    const newErrors: EditResourceFormErrors = {};

    // Required field validation
    if (!formData.name.trim()) {
      newErrors.name = 'Resource name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Resource name must be at least 2 characters';
    } else if (formData.name.trim().length > 100) {
      newErrors.name = 'Resource name must be less than 100 characters';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.trim().length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    } else if (formData.description.trim().length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
    }

    if (!formData.icon) {
      newErrors.icon = 'Please select an icon';
    }

    // Optional URL validation
    if (formData.url.trim()) {
      const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
      if (!urlPattern.test(formData.url.trim())) {
        newErrors.url = 'Please enter a valid URL';
      }
    }

    // Optional content validation
    if (formData.content.trim() && formData.content.trim().length > 1000) {
      newErrors.content = 'Content must be less than 1000 characters';
    }

    // File validation
    if (formData.files.length > 10) {
      newErrors.files = 'Maximum 10 files allowed';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Check if form has changes
  const hasChanges = useMemo(() => {
    if (!originalData) return false;
    
    return (
      formData.name !== originalData.name ||
      formData.description !== originalData.description ||
      formData.content !== originalData.content ||
      formData.url !== originalData.url ||
      formData.icon !== originalData.icon ||
      formData.files.length !== originalData.files.length
    );
  }, [formData, originalData]);

  // Show discard alert
  const showDiscardAlert = useCallback(async (onConfirm: () => void) => {
    if (hasChanges) {
      const confirmed = await showConfirm(
        'Discard Changes',
        'Are you sure you want to discard your changes?',
        'Discard',
        'Cancel'
      );
      
      if (confirmed) {
        onConfirm();
      }
    } else {
      onConfirm();
    }
  }, [hasChanges]);

  // Reset form to original values
  const resetForm = useCallback(() => {
    if (originalData) {
      setFormData(originalData);
      setErrors({});
    }
  }, [originalData]);

  return {
    formData,
    errors,
    hasChanges,
    validateForm,
    handleFieldChange,
    handleAppendFiles,
    showDiscardAlert,
    resetForm,
  };
};